import { formatCurrency, formatPercentage, formatNumber } from 'src/pages/Analytics/widgetUtils';
import { formatMetricName, formatMetricString } from 'src/helpers/commonHelper';
import { CompareDataBy, DateRange } from 'src/slices/Compare/reducer';

// Metric definitions

export interface CompareMetric {
  key: string;
  label: string;
  format: 'integer' | 'currency' | 'percentage';
  higherIsBetter: boolean;
}

/** All possible compare metrics, keyed by display_metrics identifiers. */
export const COMPARE_METRICS: CompareMetric[] = [
  { key: 'Served__c',        label: formatMetricName('Served__c'),        format: 'integer',    higherIsBetter: true  },
  { key: 'Clicks__c',        label: formatMetricName('Clicks__c'),        format: 'integer',    higherIsBetter: true  },
  { key: 'Conversions__c',   label: formatMetricName('Conversions__c'),   format: 'integer',    higherIsBetter: true  },
  { key: 'Completed_Views__c', label: formatMetricName('Completed_Views__c'), format: 'integer', higherIsBetter: true  },
  { key: 'estimated_spend',  label: formatMetricName('estimated_spend'),  format: 'currency',   higherIsBetter: true  },
  { key: 'ctr',              label: formatMetricName('ctr'),              format: 'percentage', higherIsBetter: true  },
  { key: 'completion_rate',  label: formatMetricName('completion_rate'),  format: 'percentage', higherIsBetter: true  },
  { key: 'cpc',              label: formatMetricName('cpc'),              format: 'currency',   higherIsBetter: false },
  { key: 'cpa',              label: formatMetricName('cpa'),              format: 'currency',   higherIsBetter: false },
  { key: 'cvr',              label: formatMetricName('cvr'),              format: 'percentage', higherIsBetter: true  },
  { key: 'Cost__c',          label: formatMetricName('Cost__c'),          format: 'currency',   higherIsBetter: true  },
];

/** Filter to only metrics the user has access to for this exchange. */
export function getPermittedCompareMetrics(displayMetrics: string[]): CompareMetric[] {
  const normalizedSet = new Set(displayMetrics.map(m => formatMetricString(m)));
  return COMPARE_METRICS.filter(m => normalizedSet.has(formatMetricString(m.key)));
}

// Formatters (reuse widgetUtils)

export function formatMetricValue(value: number, format: CompareMetric['format']): string {
  switch (format) {
    case 'currency':   return formatCurrency(value);
    case 'percentage': return formatPercentage(value);
    default:           return formatNumber(value);
  }
}

// % Change helpers

/**
 * Returns a color string for a given % change and metric direction.
 */
export function getChangeColor(pct: number | null, higherIsBetter: boolean): string {
  if (pct === null || pct === 0) return '#6c757d';
  const isGood = pct > 0 ? higherIsBetter : !higherIsBetter;
  return isGood ? '#00C853' : '#f46a6a';
}

/** Returns a CSS class modifier based on the direction of change. */
export function getCardModifier(pct: number | null, higherIsBetter: boolean): string {
  if (pct === null || pct === 0) return 'compare-kpi-card--neutral';
  const isGood = pct > 0 ? higherIsBetter : !higherIsBetter;
  return isGood ? 'compare-kpi-card--positive' : 'compare-kpi-card--negative';
}

/**
 * Formats a % change value with sign and caps at ±999%.
 * Returns "N/A" when value is null (range2 was 0).
 */
export function capPercentChange(value: number | null): string {
  if (value === null) return 'N/A';
  if (value > 999)  return '+999%+';
  if (value < -999) return '-999%+';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

// Date helpers (timezone-safe)

/** Parse a YYYY-MM-DD string as a local Date (avoids UTC midnight shift). */
export function parseLocalDateStr(dateStr: string): Date | null {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Add `days` calendar days to a local Date, returning a new Date. */
function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export interface DateBounds {
  /** Effective minDate for the DR1 (Current) picker. */
  dr1MinDate: Date | null;
  /** Effective maxDate for the DR2 (Previous) picker. */
  dr2MaxDate: Date | null;
  /** True when DR2 occupies so much of the order window that DR1 has no valid dates. */
  dr1WindowEmpty: boolean;
  /** True when DR1 starts so early that DR2 has no valid dates. */
  dr2WindowEmpty: boolean;
  /** True when both ranges are fully set but still overlap (e.g. after an edit). */
  rangesConflict: boolean;
}

/** Overlap-prevention bounds for the two date pickers. */
export function computeDateBounds(
  dateRange1: DateRange,
  dateRange2: DateRange,
  orderMinDate: Date | null,
  orderMaxDate: Date | null,
): DateBounds {
  // DR1 min = day after DR2 ends (clamped to orderMinDate)
  let dr1MinDate: Date | null = orderMinDate;
  if (dateRange2.end) {
    const dr2End = parseLocalDateStr(dateRange2.end);
    if (dr2End) {
      const dayAfter = addDays(dr2End, 1);
      dr1MinDate = orderMinDate && dayAfter < orderMinDate ? orderMinDate : dayAfter;
    }
  }

  // DR2 max = day before DR1 starts (clamped to orderMaxDate)
  let dr2MaxDate: Date | null = orderMaxDate;
  if (dateRange1.start) {
    const dr1Start = parseLocalDateStr(dateRange1.start);
    if (dr1Start) {
      const dayBefore = addDays(dr1Start, -1);
      dr2MaxDate = orderMaxDate && dayBefore > orderMaxDate ? orderMaxDate : dayBefore;
    }
  }

  const dr1WindowEmpty = !!dr1MinDate && !!orderMaxDate && dr1MinDate > orderMaxDate;
  const dr2WindowEmpty = !!dr2MaxDate && !!orderMinDate && dr2MaxDate < orderMinDate;

  const allSet =
    !!dateRange1.start && !!dateRange1.end &&
    !!dateRange2.start && !!dateRange2.end;
  const rangesConflict = allSet && dateRange2.end >= dateRange1.start;

  return { dr1MinDate, dr2MaxDate, dr1WindowEmpty, dr2WindowEmpty, rangesConflict };
}

/** Number of calendar days between two YYYY-MM-DD strings. */
export function daysBetween(start: string, end: string): number {
  if (!start || !end) return 0;
  const [sy, sm, sd] = start.split('-').map(Number);
  const [ey, em, ed] = end.split('-').map(Number);
  return (Date.UTC(ey, em - 1, ed) - Date.UTC(sy, sm - 1, sd)) / 86_400_000;
}

/** Available Data By options based on the longer of the two ranges. */
export function getAvailableDataBy(range1: DateRange, range2: DateRange): CompareDataBy[] {
  const maxDays = Math.max(
    daysBetween(range1.start, range1.end),
    daysBetween(range2.start, range2.end)
  );

  if (maxDays < 7)   return ['day'];
  if (maxDays < 28)  return ['day', 'week'];
  if (maxDays < 92)  return ['day', 'week', 'month'];
  if (maxDays < 365) return ['day', 'week', 'month', 'quarter'];
  return ['day', 'week', 'month', 'quarter', 'year'];
}
